import {
  Award,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  HelpCircle,
  History,
  Link as LinkIcon,
  Package,
  Plus,
  ShieldAlert,
  StickyNote,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import {
  type AdvisoryForm,
  type AdvisoryFormId,
  type AdvisoryFormReference,
  type AdvisoryFormVulnerability,
  emptyAck,
  emptyId,
  emptyReference,
  emptyRemediation,
  emptyVulnerability,
  nextProductId,
  PRODUCT_STATUS_FIELDS,
  type ProductStatusKey,
} from "../utils/csafForm";

interface Props {
  value: AdvisoryForm;
  onChange: (next: AdvisoryForm) => void;
}

const TLP_OPTIONS = ["WHITE", "GREEN", "AMBER", "RED"];
const STATUS_OPTIONS = ["draft", "interim", "final"];
const REMEDIATION_CATEGORIES = [
  "vendor_fix",
  "mitigation",
  "workaround",
  "none_available",
  "no_fix_planned",
];
const REFERENCE_CATEGORIES = ["external", "self"];

const inputClass = "input w-full";
const labelClass =
  "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";
const sectionClass = "";
const sectionTitleClass =
  "text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide";

/**
 * Ordered wizard steps. Each corresponds to one section of the guided form.
 * `required` marks steps that must have content for a valid CSAF advisory and
 * surfaces an amber dot in the navigator until they are filled.
 */
const STEPS = [
  { title: "Document", icon: FileText, required: true },
  { title: "Publisher", icon: Building2, required: false },
  { title: "Notes", icon: StickyNote, required: false },
  { title: "References", icon: LinkIcon, required: false },
  { title: "Products", icon: Package, required: true },
  { title: "Vulnerabilities", icon: ShieldAlert, required: true },
  { title: "Acknowledgments", icon: Award, required: false },
  { title: "Revision history", icon: History, required: false },
] as const;

/**
 * Plain-language explanation for each guided-form field, shown as a hover
 * tooltip. Keys match the exact label text rendered via <FieldLabel>.
 */
const HINTS: Record<string, string> = {
  "Title *":
    "A short, human-readable headline for the advisory. Maps to CSAF document.title.",
  "Tracking ID *":
    "Your unique identifier for this advisory, e.g. SANDVIK-SA-2026-0001. Maps to document.tracking.id.",
  TLP: "Traffic Light Protocol sharing level (WHITE/GREEN/AMBER/RED) that tells recipients how widely the advisory may be shared.",
  Status:
    "Lifecycle state of the advisory. 'draft' = work in progress, 'interim' = published but still changing, 'final' = complete. Maps to tracking.status.",
  Version:
    "Revision number of this advisory document (increment on each update). Maps to tracking.version.",
  "Initial release date":
    "When the advisory was first published (ISO 8601, e.g. 2026-07-10T10:00:00Z). Maps to tracking.initial_release_date.",
  "Current release date":
    "When this (latest) version was released (ISO 8601). Maps to tracking.current_release_date.",
  "Aliases (comma-separated)":
    "Other IDs this advisory is also known by, e.g. a vendor or CERT reference. Maps to tracking.aliases.",
  "Aggregate severity":
    "An overall, human-readable severity label for the whole advisory, e.g. Critical. Maps to document.aggregate_severity.text.",
  "Aggregate severity namespace (URL)":
    "Optional URL of the scoring system the aggregate severity comes from. Maps to document.aggregate_severity.namespace.",
  "Distribution / TLP text":
    "Free-text distribution or handling rules, e.g. 'Distribution of this advisory is unlimited.' Maps to document.distribution.text.",
  Name: "Name of the organization publishing the advisory, e.g. Sandvik. Maps to document.publisher.name.",
  "Namespace (URL)":
    "A URL that uniquely identifies the publisher, e.g. https://www.sandvik.com. Maps to document.publisher.namespace.",
  "Contact details":
    "How to reach the publisher, e.g. PSIRT, psirt@sandvik.com. Maps to publisher.contact_details.",
  "Issuing authority":
    "Describes the role/authority of the issuing party. Maps to publisher.issuing_authority.",
  "Rationale (summary)":
    "A summary note explaining why this advisory exists. Stored as a document note with category 'summary'.",
  "Notice (general)":
    "A general note with any additional information for readers. Stored as a document note with category 'general'.",
  "Disclaimer (legal)":
    "Legal disclaimer text. Stored as a document note with category 'legal_disclaimer'.",
  "Canonical CSAF URL (self reference)":
    "The permanent URL where this CSAF file is published. Stored as a reference with category 'self'.",
  CVE: "The CVE identifier for this vulnerability, e.g. CVE-2026-12345. Maps to vulnerabilities[].cve.",
  Title:
    "Short title describing this specific vulnerability. Maps to vulnerabilities[].title.",
  "CWE ID":
    "The Common Weakness Enumeration ID, e.g. CWE-22. Maps to vulnerabilities[].cwe.id.",
  "CWE name":
    "Human-readable name of the CWE, e.g. Improper Limitation of a Pathname. Maps to vulnerabilities[].cwe.name.",
  "CVSS v3.1 vector":
    "The CVSS v3.1 vector string, e.g. CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:N. Maps to scores[].cvss_v3.vectorString.",
  "CVSS base score":
    "The numeric CVSS base score 0.0–10.0. Severity (LOW/MEDIUM/HIGH/CRITICAL) is derived automatically. Maps to scores[].cvss_v3.baseScore.",
  "Discovery date":
    "When the vulnerability was discovered (ISO 8601, optional). Maps to vulnerabilities[].discovery_date.",
  "Release date":
    "When this vulnerability was publicly disclosed (ISO 8601, optional). Maps to vulnerabilities[].release_date.",
  Description:
    "Detailed description of the vulnerability. Stored as a vulnerability note with category 'description'.",
  "Exploitation status":
    "Notes on known exploitation, e.g. 'No known public exploit.' Stored as a threat with category 'exploit_status'.",
  "Product status":
    "For each product, mark which of the eight CSAF states applies (Affected, Fixed, Not affected, etc.). Maps to vulnerabilities[].product_status.",
  References:
    "Links to related resources for this vulnerability, e.g. NVD entry or advisory page. Maps to vulnerabilities[].references.",
  "External IDs":
    "Identifiers for this vulnerability in other systems, e.g. a GitHub advisory GHSA-… Maps to vulnerabilities[].ids.",
  Remediations:
    "Actions users should take (vendor fix, mitigation, workaround, …), optionally scoped to specific products. Maps to vulnerabilities[].remediations.",
};

/** A form label with an info icon that reveals an explanation on hover. */
function FieldLabel({ children }: { children: string }) {
  const hint = HINTS[children];
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: label wraps sibling input by layout, matching the rest of this form
    <label className={labelClass}>
      <span className="inline-flex items-center gap-1">
        {children}
        {hint ? (
          <span className="relative group inline-flex items-center align-middle">
            <HelpCircle className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 cursor-help" />
            <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-64 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-xs font-normal normal-case leading-snug shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-[9999]">
              {hint}
            </span>
          </span>
        ) : null}
      </span>
    </label>
  );
}

export default function AdvisoryFormEditor({ value, onChange }: Props) {
  const [step, setStep] = useState(0);
  const patch = (partial: Partial<AdvisoryForm>) =>
    onChange({ ...value, ...partial });

  // ---- Document references ---------------------------------------------
  const addDocRef = () =>
    patch({
      documentReferences: [...value.documentReferences, emptyReference()],
    });
  const updateDocRef = (
    index: number,
    partial: Partial<AdvisoryFormReference>,
  ) =>
    patch({
      documentReferences: value.documentReferences.map((r, i) =>
        i === index ? { ...r, ...partial } : r,
      ),
    });
  const removeDocRef = (index: number) =>
    patch({
      documentReferences: value.documentReferences.filter(
        (_, i) => i !== index,
      ),
    });

  // ---- Products ---------------------------------------------------------
  const addProduct = () =>
    patch({
      products: [
        ...value.products,
        { id: nextProductId(value.products), name: "" },
      ],
    });
  const updateProduct = (index: number, name: string) =>
    patch({
      products: value.products.map((p, i) =>
        i === index ? { ...p, name } : p,
      ),
    });
  const removeProduct = (index: number) => {
    const removed = value.products[index];
    patch({
      products: value.products.filter((_, i) => i !== index),
      // scrub references to the removed product id from every vulnerability
      vulnerabilities: removed
        ? value.vulnerabilities.map((v) => {
            const scrubbed: Partial<AdvisoryFormVulnerability> = {};
            for (const f of PRODUCT_STATUS_FIELDS) {
              scrubbed[f.key] = v[f.key].filter((id) => id !== removed.id);
            }
            return {
              ...v,
              ...scrubbed,
              remediations: v.remediations.map((r) => ({
                ...r,
                productIds: r.productIds.filter((id) => id !== removed.id),
              })),
            };
          })
        : value.vulnerabilities,
    });
  };

  // ---- Vulnerabilities --------------------------------------------------
  const addVuln = () =>
    patch({
      vulnerabilities: [...value.vulnerabilities, emptyVulnerability()],
    });
  const updateVuln = (
    index: number,
    partial: Partial<AdvisoryFormVulnerability>,
  ) =>
    patch({
      vulnerabilities: value.vulnerabilities.map((v, i) =>
        i === index ? { ...v, ...partial } : v,
      ),
    });
  const removeVuln = (index: number) =>
    patch({
      vulnerabilities: value.vulnerabilities.filter((_, i) => i !== index),
    });

  const toggleStatus = (
    vIndex: number,
    key: ProductStatusKey,
    productId: string,
  ) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    const list = vuln[key];
    const next = list.includes(productId)
      ? list.filter((id) => id !== productId)
      : [...list, productId];
    updateVuln(vIndex, { [key]: next } as Partial<AdvisoryFormVulnerability>);
  };

  // ---- Vulnerability references ----------------------------------------
  const addVulnRef = (vIndex: number) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, { references: [...vuln.references, emptyReference()] });
  };
  const updateVulnRef = (
    vIndex: number,
    rIndex: number,
    partial: Partial<AdvisoryFormReference>,
  ) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, {
      references: vuln.references.map((r, i) =>
        i === rIndex ? { ...r, ...partial } : r,
      ),
    });
  };
  const removeVulnRef = (vIndex: number, rIndex: number) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, {
      references: vuln.references.filter((_, i) => i !== rIndex),
    });
  };

  // ---- Vulnerability external IDs --------------------------------------
  const addVulnId = (vIndex: number) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, { ids: [...vuln.ids, emptyId()] });
  };
  const updateVulnId = (
    vIndex: number,
    iIndex: number,
    partial: Partial<AdvisoryFormId>,
  ) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, {
      ids: vuln.ids.map((id, i) => (i === iIndex ? { ...id, ...partial } : id)),
    });
  };
  const removeVulnId = (vIndex: number, iIndex: number) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, { ids: vuln.ids.filter((_, i) => i !== iIndex) });
  };

  // ---- Remediations (per vuln) -----------------------------------------
  const addRemediation = (vIndex: number) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, {
      remediations: [...vuln.remediations, emptyRemediation()],
    });
  };
  const updateRemediation = (
    vIndex: number,
    rIndex: number,
    partial: Partial<{
      category: string;
      details: string;
      url: string;
      date: string;
    }>,
  ) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, {
      remediations: vuln.remediations.map((r, i) =>
        i === rIndex ? { ...r, ...partial } : r,
      ),
    });
  };
  const toggleRemediationProduct = (
    vIndex: number,
    rIndex: number,
    productId: string,
  ) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    const rem = vuln.remediations[rIndex];
    if (!rem) return;
    const next = rem.productIds.includes(productId)
      ? rem.productIds.filter((id) => id !== productId)
      : [...rem.productIds, productId];
    updateVuln(vIndex, {
      remediations: vuln.remediations.map((r, i) =>
        i === rIndex ? { ...r, productIds: next } : r,
      ),
    });
  };
  const removeRemediation = (vIndex: number, rIndex: number) => {
    const vuln = value.vulnerabilities[vIndex];
    if (!vuln) return;
    updateVuln(vIndex, {
      remediations: vuln.remediations.filter((_, i) => i !== rIndex),
    });
  };

  // ---- Acknowledgments --------------------------------------------------
  const addAck = () =>
    patch({ acknowledgments: [...value.acknowledgments, emptyAck()] });
  const updateAck = (
    index: number,
    partial: Partial<{
      names: string;
      organization: string;
      summary: string;
      urls: string;
    }>,
  ) =>
    patch({
      acknowledgments: value.acknowledgments.map((a, i) =>
        i === index ? { ...a, ...partial } : a,
      ),
    });
  const removeAck = (index: number) =>
    patch({
      acknowledgments: value.acknowledgments.filter((_, i) => i !== index),
    });

  // ---- Revision history -------------------------------------------------
  const addRevision = () =>
    patch({
      revisionHistory: [
        ...value.revisionHistory,
        {
          number: String(value.revisionHistory.length + 1),
          date: new Date().toISOString(),
          summary: "",
        },
      ],
    });
  const updateRevision = (
    index: number,
    partial: Partial<{ number: string; date: string; summary: string }>,
  ) =>
    patch({
      revisionHistory: value.revisionHistory.map((r, i) =>
        i === index ? { ...r, ...partial } : r,
      ),
    });
  const removeRevision = (index: number) =>
    patch({
      revisionHistory: value.revisionHistory.filter((_, i) => i !== index),
    });

  // ---- Reusable reference-list renderer --------------------------------
  const renderReferenceList = (
    refs: AdvisoryFormReference[],
    onUpdate: (index: number, partial: Partial<AdvisoryFormReference>) => void,
    onRemove: (index: number) => void,
  ) =>
    refs.map((ref, index) => (
      <div
        // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
        key={index}
        className="grid grid-cols-1 md:grid-cols-[9rem_1fr_1.5fr_auto] gap-2 items-start"
      >
        <select
          className={inputClass}
          value={ref.category}
          onChange={(e) => onUpdate(index, { category: e.target.value })}
        >
          {REFERENCE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          className={inputClass}
          value={ref.summary}
          onChange={(e) => onUpdate(index, { summary: e.target.value })}
          placeholder="Summary"
        />
        <input
          className={inputClass}
          value={ref.url}
          onChange={(e) => onUpdate(index, { url: e.target.value })}
          placeholder="https://…"
        />
        <button
          type="button"
          className="btn-icon text-red-600 dark:text-red-400"
          title="Remove reference"
          onClick={() => onRemove(index)}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    ));

  // ---- Per-step completion state (drives the navigator dots) -----------
  const stepFilled: boolean[] = [
    Boolean(value.title.trim()) && Boolean(value.trackingId.trim()),
    Boolean(value.publisherName.trim()),
    Boolean(value.rationale || value.notice || value.disclaimer),
    Boolean(value.selfUrl.trim()) || value.documentReferences.length > 0,
    value.products.length > 0,
    value.vulnerabilities.length > 0,
    value.acknowledgments.length > 0,
    value.revisionHistory.length > 0,
  ];
  const goTo = (i: number) =>
    setStep(Math.min(STEPS.length - 1, Math.max(0, i)));
  const currentStep = STEPS[step] ?? STEPS[0];

  return (
    <div>
      {/* Step navigator */}
      <div className="mb-5">
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const filled = stepFilled[i];
            return (
              <button
                key={s.title}
                type="button"
                onClick={() => goTo(i)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  active
                    ? "bg-primary-600 text-white shadow-sm"
                    : filled
                      ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                    active
                      ? "bg-white/25"
                      : filled
                        ? "bg-primary-600 text-white"
                        : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {filled && !active ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Icon className="h-3 w-3" />
                  )}
                </span>
                <span>{s.title}</span>
                {s.required && !filled ? (
                  <span className="text-amber-500" title="Required section">
                    *
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="h-1 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Active step body */}
      <div key={step} className="space-y-2 da-question-enter">
        {step === 0 && (
          <div>
            <div className={sectionTitleClass}>Document</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <FieldLabel>Title *</FieldLabel>
                <input
                  className={inputClass}
                  value={value.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder="e.g. Product X: Path Traversal Vulnerability"
                />
              </div>
              <div>
                <FieldLabel>Tracking ID *</FieldLabel>
                <input
                  className={inputClass}
                  value={value.trackingId}
                  onChange={(e) => patch({ trackingId: e.target.value })}
                  placeholder="SANDVIK-SA-2026-0001"
                />
              </div>
              <div>
                <FieldLabel>TLP</FieldLabel>
                <select
                  className={inputClass}
                  value={value.tlp}
                  onChange={(e) => patch({ tlp: e.target.value })}
                >
                  {TLP_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Status</FieldLabel>
                <select
                  className={inputClass}
                  value={value.status}
                  onChange={(e) => patch({ status: e.target.value })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Version</FieldLabel>
                <input
                  className={inputClass}
                  value={value.version}
                  onChange={(e) => patch({ version: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel>Initial release date</FieldLabel>
                <input
                  className={inputClass}
                  value={value.initialReleaseDate}
                  onChange={(e) =>
                    patch({ initialReleaseDate: e.target.value })
                  }
                />
              </div>
              <div>
                <FieldLabel>Current release date</FieldLabel>
                <input
                  className={inputClass}
                  value={value.currentReleaseDate}
                  onChange={(e) =>
                    patch({ currentReleaseDate: e.target.value })
                  }
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Aliases (comma-separated)</FieldLabel>
                <input
                  className={inputClass}
                  value={value.aliases}
                  onChange={(e) => patch({ aliases: e.target.value })}
                  placeholder="Other IDs this advisory is also known by"
                />
              </div>
              <div>
                <FieldLabel>Aggregate severity</FieldLabel>
                <input
                  className={inputClass}
                  value={value.aggregateSeverityText}
                  onChange={(e) =>
                    patch({ aggregateSeverityText: e.target.value })
                  }
                  placeholder="e.g. Critical"
                />
              </div>
              <div>
                <FieldLabel>Aggregate severity namespace (URL)</FieldLabel>
                <input
                  className={inputClass}
                  value={value.aggregateSeverityNamespace}
                  onChange={(e) =>
                    patch({ aggregateSeverityNamespace: e.target.value })
                  }
                  placeholder="Optional scoring-system URL"
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Distribution / TLP text</FieldLabel>
                <input
                  className={inputClass}
                  value={value.distributionText}
                  onChange={(e) => patch({ distributionText: e.target.value })}
                  placeholder="e.g. Distribution of this advisory is unlimited."
                />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Publisher</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Name</FieldLabel>
                <input
                  className={inputClass}
                  value={value.publisherName}
                  onChange={(e) => patch({ publisherName: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel>Namespace (URL)</FieldLabel>
                <input
                  className={inputClass}
                  value={value.publisherNamespace}
                  onChange={(e) =>
                    patch({ publisherNamespace: e.target.value })
                  }
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Contact details</FieldLabel>
                <input
                  className={inputClass}
                  value={value.publisherContact}
                  onChange={(e) => patch({ publisherContact: e.target.value })}
                  placeholder="PSIRT, psirt@sandvik.com"
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Issuing authority</FieldLabel>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={value.issuingAuthority}
                  onChange={(e) => patch({ issuingAuthority: e.target.value })}
                  placeholder="Role of the issuing party, e.g. Sandvik PSIRT coordinates…"
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={sectionClass}>
            <div className={sectionTitleClass}>Notes</div>
            <div className="space-y-3">
              <div>
                <FieldLabel>Rationale (summary)</FieldLabel>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={value.rationale}
                  onChange={(e) => patch({ rationale: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel>Notice (general)</FieldLabel>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={value.notice}
                  onChange={(e) => patch({ notice: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel>Disclaimer (legal)</FieldLabel>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={value.disclaimer}
                  onChange={(e) => patch({ disclaimer: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
              <div className={sectionTitleClass}>References</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addDocRef}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add reference
              </button>
            </div>
            <div className="mb-3">
              <FieldLabel>Canonical CSAF URL (self reference)</FieldLabel>
              <input
                className={inputClass}
                value={value.selfUrl}
                onChange={(e) => patch({ selfUrl: e.target.value })}
                placeholder="https://www.sandvik.com/psirt/csaf/2026/..."
              />
            </div>
            {value.documentReferences.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No additional references. Add advisory pages, KB articles, etc.
              </p>
            )}
            <div className="space-y-2">
              {renderReferenceList(
                value.documentReferences,
                updateDocRef,
                removeDocRef,
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
              <div className={sectionTitleClass}>Affected products</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addProduct}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add product
              </button>
            </div>
            {value.products.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No products yet. Add the products this advisory concerns.
              </p>
            )}
            <div className="space-y-2">
              {value.products.map((product, index) => {
                const hasHelper =
                  product._source != null &&
                  "product_identification_helper" in product._source;
                return (
                  <div key={product.id} className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 w-28 shrink-0">
                      {product.id}
                    </span>
                    <input
                      className={inputClass}
                      value={product.name}
                      onChange={(e) => updateProduct(index, e.target.value)}
                      placeholder="Product name and version"
                    />
                    {hasHelper && (
                      <span
                        className="text-xs text-gray-400 dark:text-gray-500 shrink-0"
                        title="CPE / PURL / hashes etc. are preserved from the source CSAF"
                      >
                        +ID details
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn-icon text-red-600 dark:text-red-400"
                      title="Remove product"
                      onClick={() => removeProduct(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
              <div className={sectionTitleClass}>Vulnerabilities</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addVuln}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add vulnerability
              </button>
            </div>
            {value.vulnerabilities.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No vulnerabilities yet.
              </p>
            )}
            <div className="space-y-4">
              {value.vulnerabilities.map((vuln, vIndex) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and reorder-free
                  key={vIndex}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {vuln.cve || `Vulnerability ${vIndex + 1}`}
                    </span>
                    <button
                      type="button"
                      className="btn-icon text-red-600 dark:text-red-400"
                      title="Remove vulnerability"
                      onClick={() => removeVuln(vIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <FieldLabel>CVE</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.cve}
                        onChange={(e) =>
                          updateVuln(vIndex, { cve: e.target.value })
                        }
                        placeholder="CVE-2026-12345"
                      />
                    </div>
                    <div>
                      <FieldLabel>Title</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.title}
                        onChange={(e) =>
                          updateVuln(vIndex, { title: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <FieldLabel>CWE ID</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.cweId}
                        onChange={(e) =>
                          updateVuln(vIndex, { cweId: e.target.value })
                        }
                        placeholder="CWE-22"
                      />
                    </div>
                    <div>
                      <FieldLabel>CWE name</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.cweName}
                        onChange={(e) =>
                          updateVuln(vIndex, { cweName: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <FieldLabel>CVSS v3.1 vector</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.cvssVector}
                        onChange={(e) =>
                          updateVuln(vIndex, { cvssVector: e.target.value })
                        }
                        placeholder="CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:N"
                      />
                    </div>
                    <div>
                      <FieldLabel>CVSS base score</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.cvssScore}
                        onChange={(e) =>
                          updateVuln(vIndex, { cvssScore: e.target.value })
                        }
                        placeholder="7.5"
                        inputMode="decimal"
                      />
                    </div>
                    <div>
                      <FieldLabel>Discovery date</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.discoveryDate}
                        onChange={(e) =>
                          updateVuln(vIndex, { discoveryDate: e.target.value })
                        }
                        placeholder="ISO date (optional)"
                      />
                    </div>
                    <div>
                      <FieldLabel>Release date</FieldLabel>
                      <input
                        className={inputClass}
                        value={vuln.releaseDate}
                        onChange={(e) =>
                          updateVuln(vIndex, { releaseDate: e.target.value })
                        }
                        placeholder="ISO date (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <FieldLabel>Description</FieldLabel>
                    <textarea
                      className={inputClass}
                      rows={2}
                      value={vuln.description}
                      onChange={(e) =>
                        updateVuln(vIndex, { description: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <FieldLabel>Exploitation status</FieldLabel>
                    <input
                      className={inputClass}
                      value={vuln.exploitation}
                      onChange={(e) =>
                        updateVuln(vIndex, { exploitation: e.target.value })
                      }
                      placeholder="No known public exploit."
                    />
                  </div>

                  {/* Product status (8 states) */}
                  {value.products.length > 0 && (
                    <div>
                      <FieldLabel>Product status</FieldLabel>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                Product
                              </th>
                              {PRODUCT_STATUS_FIELDS.map((f) => (
                                <th
                                  key={f.key}
                                  className="px-2 py-2 text-center font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap"
                                  title={f.csaf}
                                >
                                  {f.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {value.products.map((p) => (
                              <tr key={p.id}>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                  {p.name || p.id}
                                </td>
                                {PRODUCT_STATUS_FIELDS.map((f) => (
                                  <td
                                    key={f.key}
                                    className="px-2 py-2 text-center"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={vuln[f.key].includes(p.id)}
                                      onChange={() =>
                                        toggleStatus(vIndex, f.key, p.id)
                                      }
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* CVE references + external IDs */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <FieldLabel>References</FieldLabel>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => addVulnRef(vIndex)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </button>
                      </div>
                      <div className="space-y-2">
                        {renderReferenceList(
                          vuln.references,
                          (rIndex, partial) =>
                            updateVulnRef(vIndex, rIndex, partial),
                          (rIndex) => removeVulnRef(vIndex, rIndex),
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <FieldLabel>External IDs</FieldLabel>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => addVulnId(vIndex)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </button>
                      </div>
                      <div className="space-y-2">
                        {vuln.ids.map((id, iIndex) => (
                          <div
                            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                            key={iIndex}
                            className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start"
                          >
                            <input
                              className={inputClass}
                              value={id.systemName}
                              onChange={(e) =>
                                updateVulnId(vIndex, iIndex, {
                                  systemName: e.target.value,
                                })
                              }
                              placeholder="System (e.g. GitHub)"
                            />
                            <input
                              className={inputClass}
                              value={id.text}
                              onChange={(e) =>
                                updateVulnId(vIndex, iIndex, {
                                  text: e.target.value,
                                })
                              }
                              placeholder="ID (e.g. GHSA-…)"
                            />
                            <button
                              type="button"
                              className="btn-icon text-red-600 dark:text-red-400"
                              title="Remove ID"
                              onClick={() => removeVulnId(vIndex, iIndex)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Remediations */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <FieldLabel>Remediations</FieldLabel>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => addRemediation(vIndex)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </button>
                    </div>
                    <div className="space-y-3">
                      {vuln.remediations.map((rem, rIndex) => (
                        <div
                          // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                          key={rIndex}
                          className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-[10rem_1fr_1fr_9rem_auto] gap-2 items-start">
                            <select
                              className={inputClass}
                              value={rem.category}
                              onChange={(e) =>
                                updateRemediation(vIndex, rIndex, {
                                  category: e.target.value,
                                })
                              }
                            >
                              {REMEDIATION_CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            <input
                              className={inputClass}
                              value={rem.details}
                              onChange={(e) =>
                                updateRemediation(vIndex, rIndex, {
                                  details: e.target.value,
                                })
                              }
                              placeholder="Action, e.g. Upgrade to 2.3.1"
                            />
                            <input
                              className={inputClass}
                              value={rem.url}
                              onChange={(e) =>
                                updateRemediation(vIndex, rIndex, {
                                  url: e.target.value,
                                })
                              }
                              placeholder="https://… (optional)"
                            />
                            <input
                              className={inputClass}
                              value={rem.date}
                              onChange={(e) =>
                                updateRemediation(vIndex, rIndex, {
                                  date: e.target.value,
                                })
                              }
                              placeholder="ISO date"
                            />
                            <button
                              type="button"
                              className="btn-icon text-red-600 dark:text-red-400"
                              title="Remove remediation"
                              onClick={() => removeRemediation(vIndex, rIndex)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {value.products.length > 0 && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {value.products.map((p) => (
                                <label
                                  key={p.id}
                                  className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400"
                                >
                                  <input
                                    type="checkbox"
                                    checked={rem.productIds.includes(p.id)}
                                    onChange={() =>
                                      toggleRemediationProduct(
                                        vIndex,
                                        rIndex,
                                        p.id,
                                      )
                                    }
                                  />
                                  {p.name || p.id}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 6 && (
          <div className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
              <div className={sectionTitleClass}>Acknowledgments</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addAck}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>
            <div className="space-y-2">
              {value.acknowledgments.map((ack, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-start"
                >
                  <input
                    className={inputClass}
                    value={ack.names}
                    onChange={(e) =>
                      updateAck(index, { names: e.target.value })
                    }
                    placeholder="Names (comma-separated)"
                  />
                  <input
                    className={inputClass}
                    value={ack.organization}
                    onChange={(e) =>
                      updateAck(index, { organization: e.target.value })
                    }
                    placeholder="Organization"
                  />
                  <input
                    className={inputClass}
                    value={ack.summary}
                    onChange={(e) =>
                      updateAck(index, { summary: e.target.value })
                    }
                    placeholder="for reporting…"
                  />
                  <input
                    className={inputClass}
                    value={ack.urls}
                    onChange={(e) => updateAck(index, { urls: e.target.value })}
                    placeholder="URLs (comma-separated)"
                  />
                  <button
                    type="button"
                    className="btn-icon text-red-600 dark:text-red-400"
                    title="Remove acknowledgment"
                    onClick={() => removeAck(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 7 && (
          <div className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
              <div className={sectionTitleClass}>Revision history</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addRevision}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>
            <div className="space-y-2">
              {value.revisionHistory.map((rev, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-[6rem_1fr_2fr_auto] gap-2 items-start"
                >
                  <input
                    className={inputClass}
                    value={rev.number}
                    onChange={(e) =>
                      updateRevision(index, { number: e.target.value })
                    }
                    placeholder="1"
                  />
                  <input
                    className={inputClass}
                    value={rev.date}
                    onChange={(e) =>
                      updateRevision(index, { date: e.target.value })
                    }
                    placeholder="ISO date"
                  />
                  <input
                    className={inputClass}
                    value={rev.summary}
                    onChange={(e) =>
                      updateRevision(index, { summary: e.target.value })
                    }
                    placeholder="Summary of the revision"
                  />
                  <button
                    type="button"
                    className="btn-icon text-red-600 dark:text-red-400"
                    title="Remove revision"
                    onClick={() => removeRevision(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Step navigation footer */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={step === 0}
          onClick={() => goTo(step - 1)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Step {step + 1} of {STEPS.length} — {currentStep.title}
        </span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={step === STEPS.length - 1}
          onClick={() => goTo(step + 1)}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
}
